/*!
 * 可收合的頁面區塊
 *
 * # 為什麼需要它
 *
 * 幾個資料頁把「答案」「推導過程」「圖表」「主表」全部平鋪往下捲
 *（實測：雙週獎池 2.4 屏、遊戲種類數據 3.1 屏，主表分別在 y=864 / y=1383），
 * 於是最常看的主表反而要捲一屏以上才碰得到。
 *
 * 🚫 **刻意不用「摘要／明細」這種上層 tab 來分**：那會變成第 5 層控制項，
 *    而且摘要與明細**不是互斥的閱讀目的** —— 看完「這週 Rake 180 萬」，
 *    下一個動作往往就是「哪個級別貢獻的」，切 tab 會把這個連貫動作切斷。
 *    tab 的語意是互斥視角，不是粗看 vs 細看。
 *
 * # 🔴 收合列**必須**帶關鍵數字
 *
 * 收合最大的風險是「收起來的東西會被忘記」—— 這個 repo 已經有前例
 *（雜項費用頁看不到開幕週那筆 48,000，就是因為它不在預設視野裡）。
 *
 * 所以 `summary` 是**必填**：收著的時候也要能一眼知道裡面是什麼、值不值得點開。
 * 只有一個箭頭加標題的收合列，等於把資料藏起來。
 *
 * # 用法
 *
 *   <TinyCollapsibleSection
 *     title="獎池貢獻來源"
 *     icon="fa fa-diagram-project"
 *     summary={<>基數水量 <b>10,764,021</b>（排除 AOF）</>}
 *     defaultOpen={false}
 *   >
 *     ...
 *   </TinyCollapsibleSection>
 *
 * `storageKey` 有給的話，開合狀態會記在 localStorage（每個瀏覽器各自記）。
 */
import React from "react";
import PropTypes from "prop-types";
import { COLORS } from "constant/adminTheme";

const TinyCollapsibleSection = ({
  title,
  icon,
  summary,
  children,
  defaultOpen = false,
  storageKey,
  style,
}) => {
  const [open, setOpen] = React.useState(() => {
    if (!storageKey) return defaultOpen;
    try {
      const v = localStorage.getItem(`tiny.section.${storageKey}`);
      return v === null ? defaultOpen : v === "1";
    } catch (e) {
      // 私密視窗 / 阻擋 site data 時會丟 —— 退回預設值即可
      return defaultOpen;
    }
  });

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      if (storageKey) {
        try { localStorage.setItem(`tiny.section.${storageKey}`, next ? "1" : "0"); } catch (e) { /* 同上 */ }
      }
      return next;
    });
  };

  return (
    <div
      style={{
        background: COLORS.cardBg,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 10,
        marginBottom: 14,
        ...style,
      }}
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
        style={{
          display: "flex", alignItems: "center", gap: 10, width: "100%",
          background: "transparent", border: "none", borderRadius: 10,
          padding: "10px 16px", cursor: "pointer", textAlign: "left",
          transition: "background 0.16s ease",
        }}
      >
        <i
          className={`fa fa-chevron-${open ? "down" : "right"}`}
          style={{ fontSize: 11, color: COLORS.textMuted, width: 12, flexShrink: 0 }}
        />
        {icon && <i className={icon} style={{ color: COLORS.navActive, fontSize: 14, flexShrink: 0 }} />}
        <span style={{ color: COLORS.textPrimary, fontWeight: 600, fontSize: 14, whiteSpace: "nowrap" }}>
          {title}
        </span>
        {/* 🔴 收著的時候，這一段就是使用者唯一的線索 —— 不能省 */}
        <span style={{
          color: COLORS.textMuted, fontSize: 12, marginLeft: 4,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {summary}
        </span>
        <span style={{
          marginLeft: "auto", color: COLORS.textDimmed, fontSize: 11,
          whiteSpace: "nowrap", flexShrink: 0,
        }}>
          {open ? "收合" : "展開"}
        </span>
      </button>

      {open && (
        <div style={{ padding: "0 16px 14px", borderTop: `1px solid ${COLORS.border}` }}>
          <div style={{ paddingTop: 14 }}>{children}</div>
        </div>
      )}
    </div>
  );
};

TinyCollapsibleSection.propTypes = {
  title: PropTypes.node.isRequired,
  icon: PropTypes.string,
  /** 🔴 必填：收著時顯示的關鍵數字。見檔頭說明 —— 沒有它就是把資料藏起來 */
  summary: PropTypes.node.isRequired,
  children: PropTypes.node,
  defaultOpen: PropTypes.bool,
  /** 給了就把開合狀態記在 localStorage（per browser） */
  storageKey: PropTypes.string,
  style: PropTypes.object,
};

export default TinyCollapsibleSection;
